/**
 * Start portal pages that can be handed to a lead as their one free resource.
 *
 * `value` is what gets written to `accounts/{id}.freePage` by CRM ▸ Lead Overview, and is what
 * every Start page checks to decide which single tab is unlocked. Route mapping for these values
 * lives in `src/utils/startFreePageRoutes.js`.
 *
 * Pruned Aug 2026 to only pages that still exist on /start — the old Marketing, HR, and most
 * Finance/Technology options pointed at deleted pages and sent leads to /login. Legacy accounts
 * may still store one of those retired values; `getStartFreePageLabel` humanizes them so old
 * grants still render a readable label in the CRM and on the Start dashboard.
 *
 * `hasItemSelection` marks pages where CRM ▸ Lead Overview offers a second picker to unlock
 * specific items on that page (specific sales materials, SOPs, KPIs, etc.). The account fields
 * those pickers write are listed per entry.
 */
export const START_FREE_PAGES = [
  // Sales
  { value: 'sales/resources', label: 'Sales Materials', department: 'sales', description: 'Unlock specific templates and resources from the Sales Materials library', hasItemSelection: true }, // accounts.unlockedSalesMaterials (catalogKey strings)
  { value: 'sales/close-ratio', label: 'Close Ratio', department: 'sales', description: 'Track and analyze your sales conversion rates' },
  { value: 'sales-proposals', label: 'Proposals', department: 'sales', description: 'Access our template to close more deals', hasItemSelection: true }, // accounts.unlockedProposalTabs
  { value: 'sales-referral-programs', label: 'Partner Programs', department: 'sales', description: 'Build and manage your referral network of partners' },

  // Operations
  { value: 'operations/sops', label: 'SOPs', department: 'operations', description: 'Unlock specific Standard Operating Procedures', hasItemSelection: true }, // accounts.unlockedSOPs (doc ids)
  { value: 'operations/kpis', label: 'KPIs', department: 'operations', description: 'Unlock specific key performance indicators', hasItemSelection: true }, // accounts.unlockedKPIs (doc ids)
  { value: 'operations/job-descriptions', label: 'Job Descriptions', department: 'operations', description: 'Unlock specific role descriptions and responsibilities', hasItemSelection: true }, // accounts.unlockedJobs (doc ids)
  { value: 'operations/org-chart', label: 'Org Chart', department: 'operations', description: 'Manage your organization structure' },

  // Technology
  { value: 'technology/crmtechstack', label: 'Tech Stack', department: 'technology', description: 'View and manage your technology infrastructure and software stack' },
  { value: 'technology/chatgpt', label: 'ChatGPT', department: 'technology', description: 'Unlock specific AI-powered GPT tools for your business', hasItemSelection: true }, // accounts.unlockedGPTs (doc ids)
  { value: 'technology/estimating-template', label: 'Estimating Template', department: 'technology', description: 'Unlock specific estimating template categories', hasItemSelection: true }, // accounts.unlockedEstimatingTemplates (category codes)

  // Finance
  { value: 'finance/labor-burden', label: 'Labor Burden', department: 'finance', description: 'Determine the true cost of your employees including taxes, benefits, and overhead' }
];

/** Retired values → labels, so legacy grants stored on accounts still read cleanly. */
const RETIRED_FREE_PAGE_LABELS = {
  'sales/sales-scripts': 'Sales Scripts',
  'sales/pardon-our-dust': 'Pardon Our Dust',
  'sales/awards': 'Awards',
  'sales/design-pricing': 'Design Pricing',
  'technology/automation': 'Automation',
  'technology/website-plugins': 'Website Plugins',
  'technology/ai-receptionist': 'AI Receptionist',
  'technology/ai-chatbot': 'AI Chatbot',
  'finance/debt-paydown': 'Debt Paydown',
  'finance/gross-profit-margin': 'Gross Profit Margin',
  'finance/overhead-calculator': 'Overhead Calculator',
  'finance/forecast': 'Forecast',
  'finance/health': 'Health',
  'finance/project-runway': 'Project Runway',
  'finance/cost-codes': 'Cost Codes',
  'marketing/leads-calculator': 'Leads Calculator',
  'marketing/lead-roi': 'Lead ROI',
  'marketing/website': 'Website',
  'marketing/social-media': 'Social Media',
  'marketing/paid-media': 'Paid Media',
  'marketing/newsletter': 'Newsletter',
  'marketing/reviews': 'Reviews',
  'hr/hiring': 'Hiring',
  'hr/hiring-materials': 'Hiring Materials',
  'hr/job-listings': 'Job Listings',
  'hr/onboarding-materials': 'Onboarding Materials',
  'hr/employee-handbook': 'Employee Handbook',
  'hr/company-direction': 'Company Direction'
};

/**
 * @param {string | null | undefined} freePage Value stored on `accounts/{id}.freePage`
 * @returns {string | null} Human label for the page, or null when nothing is assigned
 */
export function getStartFreePageLabel(freePage) {
  if (!freePage) return null;
  return (
    START_FREE_PAGES.find((page) => page.value === freePage)?.label ||
    RETIRED_FREE_PAGE_LABELS[freePage] ||
    freePage
  );
}
